/**
 * SharedDashboard Component
 * Public dashboard for viewing shared SEO analysis results
 * Uses the same dashboard components as the private interface in read-only mode
 */

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { SEODashboardV2 } from "./seo-dashboard/SEODashboardV2";
import { ExternalLink, ArrowRight } from "lucide-react";

function SharedDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    timeAvailable: null,
    impact: [],
    category: [],
    difficulty: [],
    searchTerm: "",
  });

  useEffect(() => {
    const fetchSharedData = async () => {
      try {
        const response = await fetch(`/api/v1/share/${token}`);
        const result = await response.json();

        if (result.success) {
          setData(result.data);

          // Track preview view if in preview mode
          if (result.data.isPreview) {
            fetch("/api/v1/analytics/preview-viewed", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                shareToken: token,
                jobId: result.data.jobId,
                timestamp: new Date().toISOString(),
              }),
            }).catch(() => {}); // Silent fail for analytics
          }
        } else {
          setError(result.error || "Failed to load shared content");
        }
      } catch (err) {
        setError("Failed to load shared content");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedData();
    }
  }, [token]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const host = urlObj.hostname.replace(/^www\./, "");
      const firstSegment = urlObj.pathname.split("/").filter(Boolean)[0];
      return firstSegment ? `${host}/${firstSegment}` : host;
    } catch {
      return url;
    }
  };

  // Preview Call-to-Action Component
  const PreviewCallToAction = ({ previewData }) => {
    const handleCTAClick = () => {
      // Track CTA click (we'll implement analytics tracking later)
      fetch("/api/v1/analytics/preview-cta-clicked", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shareToken: token,
          jobId: data.jobId,
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {}); // Silent fail for analytics

      // Redirect to signup with preview tracking
      const signupUrl = `/signup?source=preview&utm_campaign=shared_preview&utm_content=${token}`;
      window.location.href = signupUrl;
    };

    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mt-6">
        <div className="text-center">
          <div className="mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Preview Mode
            </span>
          </div>

          <h3 className="text-xl font-bold text-foreground mb-2">
            Want 100+ Actions Like These for Your Website?
          </h3>

          <p className="text-muted-foreground mb-4">
            This preview shows just {previewData.actionsShown} of{" "}
            {previewData.totalActions} total recommendations. Get your own
            comprehensive SEO analysis with personalized action items.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {previewData.totalActions}
              </div>
              <div className="text-muted-foreground">
                Total Actions Available
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {previewData.actionsShown}
              </div>
              <div className="text-muted-foreground">Shown in Preview</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {previewData.totalActions - previewData.actionsShown}+
              </div>
              <div className="text-muted-foreground">More Waiting</div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Get Your Full SEO Analysis
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>

            <p className="text-xs text-muted-foreground">
              Join thousands of websites already improving their SEO
            </p>
          </div>
        </div>
      </div>
    );
  };

  // No-op handlers for shared read-only mode
  const handleStatusChange = () => {};
  const handleMoveToProgress = () => {};
  const handleRefresh = () => {};
  const handleSummaryGenerated = () => {};

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading shared analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">🔗</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Link Not Found
          </h1>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-muted-foreground">
            This link may have expired, been revoked, or never existed.
          </p>
          <div className="mt-6">
            <a
              href="https://discoseo.com"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Visit DiscoSEO
            </a>
          </div>
        </div>
      </div>
    );
  }

  const domain = getDomainFromUrl(data.job.url);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{data.title}</h1>
            <p className="text-lg text-muted-foreground mt-1">
              {data.description}
            </p>
          </div>
          <a
            href="https://discoseo.com/"
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            Get your report
          </a>
        </div>
      </div>

      {/* Dashboard Content - Pass metadata to dashboard */}
      <div className="w-full">
        <SEODashboardV2
          actions={data.actions || []}
          executiveSummary={data.executiveSummary}
          filters={filters}
          onFiltersChange={setFilters}
          onStatusChange={handleStatusChange}
          onMoveToProgress={handleMoveToProgress}
          onRefresh={handleRefresh}
          isConnected={false}
          domain={domain}
          jobId={data.jobId}
          onSummaryGenerated={handleSummaryGenerated}
          isShared={true}
          readOnly={true}
          shareMetadata={{
            url: data.job.url,
            analyzedAt: data.job.created_at,
            completedAt: data.job.completed_at,
            viewCount: data.viewCount,
            status: data.job.status,
            sharedAt: data.createdAt,
          }}
        />

        {/* Preview CTA - Only show in preview mode */}
        {data.isPreview && data.previewMode && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <PreviewCallToAction previewData={data.previewMode} />
          </div>
        )}
      </div>

      {/* Footer */}
      {data.isPreview && (
        <div className="mt-12 text-center text-sm text-muted-foreground bg-card py-6">
          <p>Powered by DiscoSEO</p>
          <p className="mt-1">
            <button
              onClick={() => {
                const signupUrl = `/signup?source=preview&utm_campaign=shared_preview&utm_content=${token}`;
                window.location.href = signupUrl;
              }}
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Get your own comprehensive SEO analysis →
            </button>
          </p>
        </div>
      )}
    </div>
  );
}

export default SharedDashboard;
